import React, { FC, useCallback, useMemo, useState } from 'react';
import { Image as ImageUI } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { IRACVehicle } from 'modules/core/types';
import { colonFormatter, costFormatter, thousandFormatter, vinFormatter } from 'modules/core/utils';
import { Badge, Stock } from 'modules/core/components';
import { useModalState, useVehicleStatusBadgeProps } from 'modules/core/hooks';
import { ColorBadge, ColorDot } from 'modules/core/components/ColorDot/ColorDot';
import { useFormContext } from 'react-hook-form';
import { TableView } from 'modules/core/enums';
import { MODAL_DIALOG_ID, STATIC_IMAGE_URLS } from 'modules/core/constants';
import { setVehicleDetails } from 'store/reducers/vehicle.reducer';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'store/store';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { VINSpan } from 'common/styles';
import { showNotification } from 'store/reducers/ui.reducer';
import { useDealSourceData } from 'hooks/useDealSourceData';
import { TableName } from 'modules/core/enums';
import { getUser } from 'store/selectors/auth.selector';

import {
  Card,
  CardHeader,
  CardRow,
  CardDescription,
  CardImageContainer,
  CardCheckbox,
  BadgeContainer,
  ColorBadgeWrapper,
} from './styles';
import { ROUTER_PATH } from '../../../../../../router/const/routerPath.const';

export interface IRACVehicleCard {
  vehicle: IRACVehicle;
}

export const LenderVehicleCard: FC<IRACVehicleCard> = ({ vehicle, children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { handleOpenDialog } = useModalState([MODAL_DIALOG_ID.VEHICLE_DETAILS]);
  const isLender = window.location.pathname !== ROUTER_PATH.RAC_AFFORDABILITY_ENGINE;
  const { dealSourceData } = useDealSourceData(isLender ? TableName.LENDER : TableName.RAC);
  const { t } = useTranslation();
  const badgeProps = useVehicleStatusBadgeProps(vehicle.age, vehicle.is_new, vehicle.is_sold);
  const { watch } = useFormContext();
  const { tableView } = watch();
  const { noPhoto, blackCarCard, blackCarManuallyCreatedCard, whiteCarCard } = STATIC_IMAGE_URLS.icons;
  const user = useSelector(getUser);
  const isImageExist = !!vehicle.photo;

  const openVehicleDetailsDialog = useCallback(() => {
    dispatch(setVehicleDetails({ id: vehicle.id, title: vehicle.description }));
    handleOpenDialog();
  }, [handleOpenDialog, dispatch, vehicle]);

  const src = useMemo(() => {
    if (vehicle.manually_created) {
      if (Object.keys(dealSourceData || {}).length > 0 && dealSourceData.vehicle_id === vehicle.id) {
        return whiteCarCard;
      }

      return blackCarManuallyCreatedCard;
    }

    if (user?.withWholesaleInventory) {
      return isImageExist ? vehicle.photo : blackCarCard;
    }

    return isImageExist ? vehicle.photo : noPhoto;
  }, [
    vehicle.manually_created,
    vehicle.photo,
    vehicle.id,
    isImageExist,
    blackCarCard,
    noPhoto,
    dealSourceData,
    blackCarManuallyCreatedCard,
    whiteCarCard,
    user?.withWholesaleInventory,
  ]);

  const extraCash =
    typeof vehicle.ltv_value?.extra_down_payment === 'number'
      ? vehicle.ltv_value?.extra_down_payment // for RAC
      : vehicle.salesperson_bucket; // for Lenders

  const [imageSrc, setImageSrc] = useState<string>(src);

  const dialogTriggerProps = useMemo(
    () =>
      [noPhoto, whiteCarCard, blackCarCard, blackCarManuallyCreatedCard].includes(imageSrc)
        ? {}
        : { onClick: openVehicleDetailsDialog },
    [blackCarCard, blackCarManuallyCreatedCard, imageSrc, noPhoto, openVehicleDetailsDialog, whiteCarCard],
  );

  const handleError = () => {
    setImageSrc(STATIC_IMAGE_URLS.icons.noPhoto);
  };

  return (
    <Card key={vehicle.id} tableView={tableView}>
      <CardHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <CardCheckbox>{children}</CardCheckbox>
          {vehicle.manually_created ? <div></div> : <Stock stock={vehicle.stock} />}
        </div>
        <CopyToClipboard
          text={vehicle.vin}
          onCopy={() => dispatch(showNotification({ message: t('successfullyCopied'), type: 'successful' }))}
        >
          <VINSpan title={t('components.table.vinHelpMessage')}>{vinFormatter(vehicle.vin)}</VINSpan>
        </CopyToClipboard>
      </CardHeader>
      <CardImageContainer>
        <ImageUI src={imageSrc} onError={handleError} {...dialogTriggerProps} data-testid="card-rac-vehicle" />
        <BadgeContainer>{badgeProps && <Badge {...badgeProps} isCardView={true} />}</BadgeContainer>
        {user?.withWholesaleInventory && (vehicle.color || vehicle.color_canonical) && (
          <ColorBadgeWrapper>
            <ColorBadge canonical={vehicle.color_canonical} label={vehicle.color} size={16} />
          </ColorBadgeWrapper>
        )}
      </CardImageContainer>
      <CardDescription>{vehicle.description}</CardDescription>
      {user?.withWholesaleInventory && (vehicle.color || vehicle.color_canonical) && (
        <CardRow>
          <span>{colonFormatter(t('components.table.columns.color'))}</span>
          <ColorDot canonical={vehicle.color_canonical} label={vehicle.color} />
        </CardRow>
      )}
      <CardRow>
        <span>{colonFormatter(t('components.table.columns.location'))}</span>
        <span>{vehicle.location}</span>
      </CardRow>
      <CardRow>
        <span>{colonFormatter(t('components.table.columns.miles'))}</span>
        <span>{thousandFormatter(vehicle.odometer)}</span>
      </CardRow>
      <CardRow>
        <span>{colonFormatter(t('components.table.columns.retailPrice'))}</span>
        <span>{costFormatter(vehicle.current_price)}</span>
      </CardRow>
      {tableView === TableView.SALESPERSON && typeof extraCash === 'number' && (
        <CardRow>
          <span>{colonFormatter(t('components.table.columns.extraCash'))}</span>
          <span>{costFormatter(extraCash)}</span>
        </CardRow>
      )}
    </Card>
  );
};
